// src/Component/Dashboard/Dashboard.jsx
import React, { useEffect, useState } from "react";
import { jwtDecode } from "jwt-decode";
import {
  Users,
  MessageSquare,
  FileText,
  ShieldCheck,
  LogOut,
  TrendingUp,
  AlertCircle,
  Plus,
  Image,
  Calendar,
  FolderKanban,
  Mail,
  Briefcase
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";

const customStyles = `
  @keyframes smoothSlideUp {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .slide-up {
    animation: smoothSlideUp 0.4s ease-out forwards;
  }
`;

const AnimatedSection = ({ children, delayMs = 0 }) => (
  <div className="slide-up opacity-0" style={{ animationDelay: `${delayMs}ms` }}>
    {children}
  </div>
);

function StatCard({ title, value, subtext, icon: Icon, onClick }) {
  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200 group flex flex-col justify-between min-h-[140px]"
    >
      <div className="flex items-center justify-between w-full">
        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">{title}</span>
        <Icon className="w-5 h-5 text-amber-500 group-hover:scale-110 transition-transform duration-200" />
      </div>
      <div>
        <p className="text-3xl font-bold text-slate-900 tracking-tight">{value}</p>
        {subtext && <p className="text-xs text-slate-500 mt-1">{subtext}</p>}
      </div>
    </button>
  );
}

export default function Dashboard() {
  const [adminName, setAdminName] = useState("Admin");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [authError, setAuthError] = useState(null);

  const [stats, setStats] = useState({
    teamMembers: "0",
    portfolios: "0",
    inquiries: "0",
    testimonials: "0",
    news: "0",
    events: "0",
    gallery: "0",
    jobs: "0",
    applications: "0"
  });

  const navigate = useNavigate();
  const base = process.env.REACT_APP_API_BASE || "";

  useEffect(() => {
    const token = localStorage.getItem("admin-auth");
    if (!token) {
      setAuthError("No active session. Please log in.");
      return;
    }
    try {
      const decoded = jwtDecode(token);
      const now = Date.now() / 1000;
      if (decoded.exp && decoded.exp < now) {
        setAuthError("Your session has expired. Please log in again.");
        return;
      }
      setAdminName(decoded.name || decoded.email || "Admin");
      setIsLoggedIn(true);
    } catch {
      setAuthError("Invalid session token. Please log in again.");
    }
  }, []);

  const fetchDashboardData = () => {
    if (!isLoggedIn) return;

    Promise.allSettled([
      fetch(`${base}/team-members`).then((r) => r.json()),
      fetch(`${base}/portfolios`).then((r) => r.json()),
      fetch(`${base}/inquiries`).then((r) => r.json()),
      fetch(`${base}/testimonials`).then((r) => r.json()),
      fetch(`${base}/news`).then((r) => r.json()),
      fetch(`${base}/events`).then((r) => r.json()),
      fetch(`${base}/gallery`).then((r) => r.json()),
      fetch(`${base}/join/jobs/admin-summary`).then((r) => r.json()),
    ]).then(([team, port, inq, test, newsData, ev, gal, jobs]) => {
      let totalApplications = 0;
      if (jobs.status === "fulfilled" && Array.isArray(jobs.value)) {
        totalApplications = jobs.value.reduce((sum, job) => sum + (job.total_applications || 0), 0);
      }

      setStats({
        teamMembers: team.status === "fulfilled" && team.value?.success ? String(team.value.teamMembers?.length || 0) : "0",
        portfolios: port.status === "fulfilled" && port.value?.success ? String(port.value.portfolios?.length || 0) : "0",
        inquiries: inq.status === "fulfilled" && inq.value?.success ? String(inq.value.inquiries?.length || 0) : "0",
        testimonials: test.status === "fulfilled" && test.value?.success ? String(test.value.testimonials?.length || 0) : "0",
        news: newsData.status === "fulfilled" && newsData.value?.success ? String(newsData.value.news?.length || 0) : "0",
        events: ev.status === "fulfilled" && ev.value?.success ? String(ev.value.events?.length || 0) : "0",
        gallery: gal.status === "fulfilled" && gal.value?.success ? String(gal.value.gallery?.length || 0) : "0",
        jobs: jobs.status === "fulfilled" ? String(jobs.value?.length || 0) : "0",
        applications: String(totalApplications),
      });
    });
  };

  useEffect(() => {
    fetchDashboardData();
  }, [isLoggedIn]);

  const handleLogout = () => {
    localStorage.removeItem("admin-auth");
    navigate("/AdminLogin");
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
        <div className="bg-white p-8 rounded-xl border border-slate-200 text-center max-w-sm shadow-md">
          <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4 border border-red-100">
            <AlertCircle className="w-6 h-6 text-red-500" />
          </div>
          <h2 className="text-lg font-bold text-slate-800 mb-1">Access Denied</h2>
          <p className="text-slate-500 text-xs font-medium leading-relaxed mb-6">{authError || "Please log in to continue."}</p>
          <button onClick={() => navigate("/AdminLogin")} className="w-full bg-[#194393] text-white py-2.5 rounded-lg text-xs font-semibold hover:bg-[#123373] transition-colors shadow-sm">Go to Login</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{customStyles}</style>

      <div className="min-h-screen bg-slate-50 pb-20 px-4 sm:px-8">
        <div className="max-w-6xl mx-auto space-y-6 pt-6">

          {/* Header */}
          <AnimatedSection delayMs={0}>
            <header className="bg-white rounded-xl p-4 shadow-sm border border-slate-200 flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <img src={logo} alt="Indiv TechnoSoft" className="h-10 w-auto object-contain" />
                <div>
                  <h1 className="text-base font-bold text-[#194393]">Admin Dashboard</h1>
                  <p className="text-xs text-slate-500">Logged in as: <span className="text-slate-800 font-semibold">{adminName}</span></p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 px-3 py-1 rounded-full">
                  <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
                  <span className="text-[11px] font-semibold text-emerald-700">Authenticated</span>
                </div>
                <button onClick={handleLogout} className="flex items-center gap-1.5 bg-slate-100 hover:bg-red-50 hover:text-red-600 border border-slate-200 px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 transition-all">
                  <LogOut className="w-3 h-3" /> Logout
                </button>
              </div>
            </header>
          </AnimatedSection>

          {/* Welcome Banner */}
          <AnimatedSection delayMs={50}>
            <div className="bg-[#194393] rounded-xl p-5 shadow-sm text-white">
              <h2 className="text-lg font-bold">Welcome back! 👋</h2>
              <p className="text-blue-100 text-xs mt-1">Here is an overview of your website content and job applications.</p>
            </div>
          </AnimatedSection>

          {/* Core Analytics Cards */}
          <AnimatedSection delayMs={100}>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <StatCard title="Team Members" value={stats.teamMembers} subtext="Active staff profiles" icon={Users} onClick={() => navigate("/team-members")} />
              <StatCard title="Projects" value={stats.portfolios} subtext="Portfolio items published" icon={FolderKanban} onClick={() => navigate("/portfolios")} />
              <StatCard title="Inquiries" value={stats.inquiries} subtext="Client contact messages" icon={Mail} onClick={() => navigate("/inquiries")} />
            </div>
          </AnimatedSection>

          {/* Recruitment Banner */}
          <AnimatedSection delayMs={125}>
            <button
              onClick={() => navigate("/recruitment")}
              className="w-full text-left bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md hover:border-slate-300 transition-all duration-200 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 group"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-blue-50 text-[#194393]">
                  <Briefcase className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm font-bold text-slate-800">Recruitment & Careers</h3>
                  <p className="text-xs text-slate-500 mt-0.5">Manage job openings, internships, and view submitted applications.</p>
                </div>
              </div>
              <div className="text-left sm:text-right sm:border-l sm:pl-6 border-slate-200 min-w-[120px]">
                <p className="text-2xl font-bold text-[#194393]">{stats.applications}</p>
                <p className="text-xs font-medium text-slate-500">Total Applications</p>
              </div>
            </button>
          </AnimatedSection>

          {/* Secondary Stats */}
          <AnimatedSection delayMs={150}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <MessageSquare className="w-4 h-4 mb-2 text-slate-400" />
                <p className="text-xs font-medium text-slate-500">Testimonials</p>
                <p className="text-xl font-bold text-slate-800 mt-1">{stats.testimonials}</p>
              </div>
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <FileText className="w-4 h-4 mb-2 text-slate-400" />
                <p className="text-xs font-medium text-slate-500">News Articles</p>
                <p className="text-xl font-bold text-slate-800 mt-1">{stats.news}</p>
              </div>
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
                <Calendar className="w-4 h-4 mb-2 text-slate-400" />
                <p className="text-xs font-medium text-slate-500">Events</p>
                <p className="text-xl font-bold text-slate-800 mt-1">{stats.events}</p>
              </div>
              <div className="bg-white p-4 rounded-xl border border-slate-200 shadow-sm col-span-2 sm:col-span-1">
                <Image className="w-4 h-4 mb-2 text-slate-400" />
                <p className="text-xs font-medium text-slate-500">Gallery Items</p>
                <p className="text-xl font-bold text-slate-800 mt-1">{stats.gallery}</p>
              </div>
            </div>
          </AnimatedSection>

          {/* Quick Actions */}
          <AnimatedSection delayMs={200}>
            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <div className="flex items-center gap-2 mb-4 pb-3 border-b border-slate-100">
                <TrendingUp className="text-amber-500" size={16} />
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Quick Actions</h3>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <button onClick={() => navigate("/team-members")} className="flex items-center justify-center gap-2 w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-medium text-xs rounded-lg transition-colors shadow-sm">
                  <Plus className="w-4 h-4" /> Add Team Member
                </button>
                <button onClick={() => navigate("/portfolios")} className="flex items-center justify-center gap-2 w-full py-2.5 bg-[#194393] hover:bg-[#123373] text-white font-medium text-xs rounded-lg transition-colors shadow-sm">
                  <Plus className="w-4 h-4" /> Add Project
                </button>
                <button onClick={() => navigate("/recruitment")} className="flex items-center justify-center gap-2 w-full py-2.5 bg-slate-800 hover:bg-slate-900 text-white font-medium text-xs rounded-lg transition-colors shadow-sm">
                  Manage Recruitment
                </button>
              </div>
            </div>
          </AnimatedSection>

        </div>
      </div>
    </>
  );
}